import { useCallback, useMemo, useState } from 'react'
import { NestedMultiselectFilterItem } from '../../NestedMultiselectFilter'
import { DataTableScope, useDataTableFiltersStorage } from '../../../hooks/useDataTableFiltersStorage'
import {
    applyFilters,
    applySearch,
    buildFilterEntry,
    FilterDefinition,
    getFilterColumnId,
    SearchValuesByColumnId,
    toUncheckedOptionIds,
    UncheckedOptionIdsByFilterId,
} from '../../../utils/tableFilters'

const FILTER_ITEM_ID_SEPARATOR = '::'

const toFilterItemId = (filterId: string, optionId: string) => `${filterId}${FILTER_ITEM_ID_SEPARATOR}${optionId}`

const parseFilterItemId = (filterItemId: string) => {
    const separatorIndex = filterItemId.indexOf(FILTER_ITEM_ID_SEPARATOR)

    if (separatorIndex < 0) {
        return null
    }

    return {
        filterId: filterItemId.slice(0, separatorIndex),
        optionId: filterItemId.slice(separatorIndex + FILTER_ITEM_ID_SEPARATOR.length),
    }
}

const NOTHING_UNCHECKED: UncheckedOptionIdsByFilterId = {}

const IGNORE_UNCHECKED = () => undefined

export type DataTableFiltersState = {
    searchQuery: string
    setSearchQuery: (searchQuery: string) => void
    unchecked: UncheckedOptionIdsByFilterId
    setUnchecked: (unchecked: UncheckedOptionIdsByFilterId) => void
}

/**
 * The filters on their own, so expanded-row tables keep them when a different row is opened, and
 * across a reload. The search text stays local: a restored stale query reads as a table missing rows.
 */
export const useDataTableFiltersState = (scope: DataTableScope): DataTableFiltersState => {
    const [searchQuery, setSearchQuery] = useState('')
    const { unchecked, setUnchecked } = useDataTableFiltersStorage(scope)

    return { searchQuery, setSearchQuery, unchecked, setUnchecked }
}

export const useDataTableSearchState = (): DataTableFiltersState => {
    const [searchQuery, setSearchQuery] = useState('')

    return { searchQuery, setSearchQuery, unchecked: NOTHING_UNCHECKED, setUnchecked: IGNORE_UNCHECKED }
}

type UseDataTableFiltersArgs<TRow> = {
    rows: TRow[]
    filterDefinitions: FilterDefinition<TRow>[]
    getSearchValues: (row: TRow) => SearchValuesByColumnId
    state: DataTableFiltersState
}

export const useDataTableFilters = <TRow>({
    rows,
    filterDefinitions,
    getSearchValues,
    state,
}: UseDataTableFiltersArgs<TRow>) => {
    const { searchQuery, setSearchQuery, unchecked, setUnchecked } = state

    const filterEntries = useMemo(
        () => filterDefinitions.map((definition) => buildFilterEntry(definition, rows, unchecked)),
        [rows, filterDefinitions, unchecked]
    )

    const filterItems = useMemo(
        (): NestedMultiselectFilterItem[] =>
            filterEntries
                .filter((entry) => entry.mode !== 'noOptions')
                .map((entry) =>
                    entry.definition.columnOnly
                        ? {
                              id: toFilterItemId(entry.definition.id, entry.options[0].id),
                              label: entry.definition.label,
                          }
                        : {
                              id: entry.definition.id,
                              label: entry.definition.label,
                              children: entry.options.map((option) => ({
                                  id: toFilterItemId(entry.definition.id, option.id),
                                  label: option.label,
                              })),
                          }
                ),
        [filterEntries]
    )

    const selectedItemIds = useMemo(
        () =>
            filterEntries.flatMap((entry) =>
                entry.selectedOptionIds.map((optionId) => toFilterItemId(entry.definition.id, optionId))
            ),
        [filterEntries]
    )

    const handleSelectionChange = useCallback(
        (nextSelectedItemIds: string[]) => {
            const selectedByFilterId = nextSelectedItemIds.reduce<Record<string, string[]>>((acc, filterItemId) => {
                const parsedItemId = parseFilterItemId(filterItemId)

                if (parsedItemId) {
                    acc[parsedItemId.filterId] = (acc[parsedItemId.filterId] ?? []).concat(parsedItemId.optionId)
                }

                return acc
            }, {})

            const nextUnchecked = { ...unchecked }

            filterEntries.forEach((entry) => {
                if (entry.mode === 'noOptions') return

                nextUnchecked[entry.definition.id] = toUncheckedOptionIds(
                    entry.options,
                    selectedByFilterId[entry.definition.id] ?? [],
                    unchecked[entry.definition.id]
                )
            })

            setUnchecked(nextUnchecked)
        },
        [filterEntries, unchecked, setUnchecked]
    )

    /** Switched-off filters are not counted here: they hide a column rather than narrow rows. */
    const appliedFiltersCount = useMemo(
        () => filterEntries.filter((entry) => entry.mode === 'narrowing').length,
        [filterEntries]
    )

    const hiddenColumnIds = useMemo(
        () => filterEntries.filter((entry) => entry.mode === 'off').map((entry) => getFilterColumnId(entry.definition)),
        [filterEntries]
    )

    /** Opt-in columns are left out: one that was never shown needs no explanation for its absence. */
    const hiddenColumnsCount = useMemo(
        () => filterEntries.filter((entry) => entry.mode === 'off' && !entry.definition.defaultHidden).length,
        [filterEntries]
    )

    const visibleRows = useMemo(
        () => applyFilters(applySearch(rows, searchQuery, getSearchValues, hiddenColumnIds), filterEntries),
        [rows, searchQuery, getSearchValues, hiddenColumnIds, filterEntries]
    )

    const handleResetFilters = useCallback(() => setUnchecked({}), [setUnchecked])

    const canResetFilters = useMemo(
        () =>
            filterEntries.some((entry) =>
                entry.definition.defaultHidden
                    ? entry.selectedOptionIds.length > 0
                    : entry.selectedOptionIds.length < entry.options.length
            ),
        [filterEntries]
    )

    const hasActiveFilters = appliedFiltersCount > 0 || !!searchQuery.trim()

    return {
        toolbarProps: {
            searchQuery,
            onSearchQueryChange: setSearchQuery,
            filterItems,
            selectedItemIds,
            onSelectionChange: handleSelectionChange,
            appliedFiltersCount,
            hiddenColumnsCount,
            onResetFilters: handleResetFilters,
            canResetFilters,
        },
        hiddenColumnIds,
        hasActiveFilters,
        visibleRows,
    }
}
